import { Capacitor } from '@capacitor/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { FlexColumn, FlexRow, Margin, Padding } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3, H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { AppInfo } from '@/capacitor/components/app-info/AppInfo'
import { openAppSettings } from '@/capacitor/utils/app-settings'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { mixpanel } from '@/mixpanel'
import { LogOutAction } from '@/modules/auth/components/log-out-action'
import { useBiometrics } from '@/modules/biometrics/hooks/useBiometrics'
import { useBiometricsPermission } from '@/modules/biometrics/hooks/useBiometricsPermission'
import { BiometricsAppPermission } from '@/modules/biometrics/state/biometrics.atom'
import { AgendaSyncModal } from '@/modules/shared/components/agenda-sync-modal/AgendaSyncModal'
import { DeactivateAccountModal } from '@/modules/shared/components/deactivate-account-modal/DeactivateAccountModal'
import { DeleteAccountModal } from '@/modules/shared/components/delete-account-modal/DeleteAccountModal'
import { ToggleSwitch } from '@/modules/shared/components/toggle-switch/ToggleSwitch'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { isAndroid, isIOS } from '@/utils/environment'
import { PageLink } from '../../components/page-link/PageLink'

import type { ReactNode } from 'react'

const ProfileSettingsPage = () => {
  // -- State --
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [showDeactivateAccountModal, setShowDeactivateAccountModal] = useState<boolean>(false)
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState<boolean>(false)

  // -- Hooks --
  const theme = useTheme()
  const navigate = useNavigate()
  const { isAvailable, requestBiometricPermission } = useBiometrics()
  const { biometricPermission, updateBiometricsPermission } = useBiometricsPermission()

  const isLoggedInForMandatee = useLoggedInForMandatee()

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  const handleHideDeactivateAccountModal = () => {
    mixpanel.track('account_deactivate_cancel')
    setShowDeactivateAccountModal(false)
  }

  const handleHideSyncModal = () => {
    mixpanel.track('account_sync_cancel')
    setShowSyncModal(false)
  }

  const handleHideDeleteAccountModal = () => {
    mixpanel.track('account_delete_cancel')
    setShowDeleteAccountModal(false)
  }

  const onSyncAgendaIntent = () => {
    mixpanel.track('account_sync_show_modal')
    setShowSyncModal(true)
  }

  const onDeleteAccountIntent = () => {
    mixpanel.track('account_delete_show_confirmation_modal')
    setShowDeleteAccountModal(true)
  }

  const onDeactivateAccountIntent = () => {
    mixpanel.track('account_deactivate_show_confirmation_modal')
    setShowDeactivateAccountModal(true)
  }

  const handleDeactivate = async () => {
    handleHideDeactivateAccountModal()
  }

  const handleDelete = async () => {
    mixpanel.track('account_delete_support_called')
  }

  const requestPermission = async () => {
    if (biometricPermission === BiometricsAppPermission.GRANTED) {
      updateBiometricsPermission(BiometricsAppPermission.DENIED)
      return
    }

    const permissionGranted = await requestBiometricPermission()

    updateBiometricsPermission(permissionGranted ? BiometricsAppPermission.GRANTED : BiometricsAppPermission.DENIED)

    return permissionGranted
  }

  // Render Methods
  // ------------------------------------------------------------------------- /

  const renderBiometricsSetting = () => {
    if (!Capacitor.isNativePlatform()) {
      return null
    }

    if (isLoggedInForMandatee) {
      return (
        <>
          <H4 style={{ marginBottom: 0 }}>
            {t({
              id: 'profile.account-settings.mandate-not-allowed',
              comment: 'Mandate not allowed',
              message: 'Inloggen met vingerafdruk of gezichtsherkenning',
            })}
          </H4>
          <Span type='BodyMediumRegular'>
            {t({
              id: 'profile.account-settings.mandate-not-allowed-description',
              comment: 'Mandate not allowed description',
              message:
                'Als je inlogt via een mandaat, kan je deze functie niet gebruiken. Op je eigen profiel werkt het wel.',
            })}
          </Span>
          <Margin mb={theme.UI.Spacing.Space8} />
        </>
      )
    }

    let biometricsCta: ReactNode = null

    // Show switch on Android
    if (isAndroid() && isAvailable) {
      const handlePermissionsChanged = async () => {
        // Request permission to use biometrics when toggling on
        const hasPermissionToUpdate = await requestPermission()

        mixpanel.track('biometrics_permission_toggle_android', {
          enabled: true,
        })

        if (!hasPermissionToUpdate) {
          return
        }

        // Revoke permission when toggling off
        mixpanel.track('biometrics_permission_toggle_android', {
          enabled: false,
        })
      }
      biometricsCta = (
        <FlexRow justifyContent='space-between'>
          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest}>
            {t({
              id: 'profile.account-settings.biometrics-android',
              comment: 'Toggle Biometrics Android',
              message: 'Activeren',
            })}
          </Span>

          <ToggleSwitch
            checked={biometricPermission === BiometricsAppPermission.GRANTED}
            onChange={handlePermissionsChanged}
          />
        </FlexRow>
      )
    }

    // Show link to app settings on iOS
    if (isIOS()) {
      const openSettings = async () => {
        mixpanel.track('biometrics_permission_toggle_ios')
        await openAppSettings()
      }

      biometricsCta = (
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <Span type='BodyMediumRegular' style={{ fontWeight: 400 }}>
            {t({
              id: 'profile.account-settings.biometrics-ios-description',
              comment: 'Biometrics description for iOS',
              message:
                'Als je toestel deze functionaliteit ondersteunt, kan je die activeren of wijzigen via de telefooninstellingen.',
            })}
          </Span>
          <PageLink icon={['fal', 'external-link']} onClick={openSettings}>
            {t({
              id: 'profile.account-settings.biometrics-ios',
              comment: 'Change biometrics settings in iOS',
              message: 'Ga naar instellingen',
            })}
          </PageLink>
          {isAvailable && (
            <FlexRow justifyContent='space-between'>
              <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest}>
                {t({
                  id: 'profile.account-settings.toggle-iometrics-ios',
                  comment: 'Toggle biometrics settings in iOS',
                  message: 'Activeren',
                })}
              </Span>
              <ToggleSwitch
                checked={biometricPermission === BiometricsAppPermission.GRANTED}
                onChange={requestPermission}
              />
            </FlexRow>
          )}
        </FlexColumn>
      )
    }

    if (biometricsCta) {
      return (
        <>
          <H4 style={{ marginBottom: 0 }}>
            {t({
              id: 'profile.account-settings.biometrics',
              comment: 'Biometrics title',
              message: 'Inloggen met vingerafdruk of gezichtsherkenning',
            })}
          </H4>

          {biometricsCta}

          <Line />
          <Margin mb={theme.UI.Spacing.Space8} />
        </>
      )
    }

    return null
  }

  return (
    <ContentPageLayout
      noScroll
      header={
        <AppHeader
          primaryAction={{
            label: i18n._(globalDefinitions.goBack),
            icon: ['far', 'chevron-left'],
            onClick: () => {
              navigate('/profile', { replace: true })
            },
          }}
        />
      }
    >
      <Padding py={theme.UI.Spacing.Space4} style={{ display: 'flex', flexGrow: 1 }}>
        <FlexColumn flexGrow={1} alignItems='space-between'>
          <FlexColumn flexGrow={1} gap={theme.UI.Spacing.Space3}>
            {/* TITLE */}
            <H3 noMargin>
              {t({
                id: 'profile.account-settings.title',
                comment: 'Account settings',
                message: 'Accountinstellingen',
              })}
            </H3>

            {/* AGENDA */}
            <H4 style={{ marginBottom: 0 }}>
              {t({
                id: 'profile.account-settings.agenda',
                comment: 'Agenda',
                message: 'Agenda',
              })}
            </H4>
            <PageLink icon={['fal', 'external-link']} onClick={onSyncAgendaIntent}>
              {t({
                id: 'profile.account-settings.sync-agenda',
                comment: 'Sync agenda',
                message: 'Agenda synchroniseren',
              })}
            </PageLink>
            <Line />
            <Margin mb={theme.UI.Spacing.Space8} />

            {/* BIOMETRICS SETTINGS */}

            {renderBiometricsSetting()}

            {/* PROFILE SETTINGS */}
            <H4 style={{ marginBottom: 0 }}>
              {t({
                id: 'profile.account-settings.profile',
                comment: 'Profile',
                message: 'Profiel',
              })}
            </H4>
            <LogOutAction>
              <PageLink icon={['fal', 'arrow-right-from-bracket']} renderRaw>
                {t({
                  id: 'profile.account-settings.log-out',
                  comment: 'Log out',
                  message: 'Afmelden',
                })}
              </PageLink>
            </LogOutAction>
            <Line />

            <PageLink icon={['fal', 'pause']} onClick={onDeactivateAccountIntent}>
              {t({
                id: 'profile.account-settings.deactivate-account',
                comment: 'Deactivate account',
                message: 'Account deactiveren',
              })}
            </PageLink>
            <Line />

            <PageLink icon={['fal', 'trash-can']} onClick={onDeleteAccountIntent}>
              {t({
                id: 'profile.account-settings.delete-account',
                comment: 'Delete account',
                message: 'Account verwijderen',
              })}
            </PageLink>
            <Line />

            <Margin mb={theme.UI.Spacing.Space8} />

            <DeactivateAccountModal
              isVisible={showDeactivateAccountModal}
              onClose={handleHideDeactivateAccountModal}
              onAction={handleDeactivate}
            />

            <DeleteAccountModal
              isVisible={showDeleteAccountModal}
              onClose={handleHideDeleteAccountModal}
              onAction={handleDelete}
            />

            <AgendaSyncModal isVisible={showSyncModal} onClose={handleHideSyncModal} />
          </FlexColumn>

          <AppInfo />
        </FlexColumn>
      </Padding>
    </ContentPageLayout>
  )
}

export { ProfileSettingsPage }
